import { Box, styled, SxProps, Theme, Typography } from '@mui/material';
import { ComponentProps, MouseEventHandler, ReactNode } from 'react';

import { Badge, BadgeVariants } from '@/components/notifications/Badge/Badge';

import {
  commonTabStyle,
  getActiveStyle,
  hoverStyle,
  inactiveStyle,
} from './Tabs.utils';
import { TabValidityIndicator } from './TabValidityIndicator';

const StyledButton = styled('button')(({ theme }) => ({
  ...commonTabStyle,
  ...inactiveStyle,
  border: 'none',
  backgroundColor: 'transparent',
  fontFamily: 'inherit',
  cursor: 'pointer',

  '&:hover': hoverStyle,
  '&:disabled': { pointerEvents: 'none' },

  // Listed last so active wins the equal-specificity tie with &:hover.
  '&[aria-pressed="true"]': getActiveStyle('tab1', theme),
  '&[data-variant="tab2"][aria-pressed="true"]': getActiveStyle('tab2', theme),
}));

export interface ButtonTabProps extends ComponentProps<'button'> {
  isActive?: boolean;
  onClick: MouseEventHandler<HTMLButtonElement> | undefined;
  display: ReactNode;
  status?: 'pending' | 'complete';
  badge?: string;
  variant?: 'tab1' | 'tab2';
  disabled?: boolean;
  sx?: SxProps<Theme>;
  style?: React.CSSProperties;
  value?: string;
}

export function ButtonTab({
  isActive,
  display,
  onClick,
  status,
  badge,
  variant = 'tab1',
  style,
  ...btnProps
}: ButtonTabProps) {
  return (
    <StyledButton
      type="button"
      data-variant={variant}
      style={style}
      aria-pressed={isActive}
      onClick={onClick}
      {...btnProps}
    >
      <Box display="flex" alignItems="center" minHeight={20}>
        <Typography whiteSpace="pre-wrap" variant="body-xs" color="inherit">
          {display}
        </Typography>
        {status && (
          <Box ml={1}>
            <TabValidityIndicator isValid={status === 'complete'} />
          </Box>
        )}
        {badge && (
          <Box ml={1}>
            <Badge
              variant={
                isActive ? BadgeVariants.Primary : BadgeVariants.SecondaryFilled
              }
              display={badge}
            />
          </Box>
        )}
      </Box>
    </StyledButton>
  );
}
